import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

import "./UserSpaces.scss";

const getInitials = (name) => {
    if (!name) {
        return "";
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const resolveBandIconUrl = (band) => {
    const iconUrl = typeof band?.iconUrl === "string" ? band.iconUrl.trim() : "";
    if (!iconUrl) {
        return null;
    }
    return resolveMediaUrl(iconUrl);
};

export default function UserSpaces({
    bands,
    selectedBandId = null,
    onSelectBand,
    onCreateBand,
}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const selectedBand = useMemo(
        () => bands.find((band) => band.id === selectedBandId) || null,
        [bands, selectedBandId]
    );
    const selectedBandIconUrl = useMemo(
        () => resolveBandIconUrl(selectedBand),
        [selectedBand]
    );

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleClickOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, []);

    const handleSelect = (bandId) => {
        setIsOpen(false);
        if (bandId && bandId !== selectedBandId) {
            onSelectBand?.(bandId);
        }
    };

    const handleCreate = () => {
        setIsOpen(false);
        onCreateBand?.();
    };

    const initials = selectedBand ? getInitials(selectedBand.name) : "";
    const triggerAriaLabel = selectedBand
        ? t("bands.userSpaces.triggerAria", { name: selectedBand.name })
        : t("bands.userSpaces.triggerAriaEmpty");

    return (
        <div className={classNames("user-spaces", { "is-open": isOpen })} ref={containerRef}>
            <button
                type="button"
                className="user-spaces__trigger"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={triggerAriaLabel}
            >
                <span className="user-spaces__swatch" aria-hidden="true">
                    {selectedBandIconUrl ? (
                        <img
                            src={selectedBandIconUrl}
                            alt=""
                            className="user-spaces__swatch-image"
                        />
                    ) : (initials || "•")}
                </span>
                <span className="user-spaces__details">
                    <span className="user-spaces__label">{t("bands.userSpaces.label")}</span>
                    <span className="user-spaces__name">{selectedBand?.name || t("bands.userSpaces.placeholder")}</span>
                </span>
                <span className="user-spaces__chevron" aria-hidden="true">▾</span>
            </button>
            {isOpen && (
                <div className="user-spaces__dropdown" role="menu">
                    <div className="user-spaces__dropdown-body">
                        {bands.length ? (
                            <ul className="user-spaces__list">
                                {bands.map((band) => {
                                    const optionIconUrl = resolveBandIconUrl(band);
                                    return (
                                        <li key={band.id}>
                                            <button
                                                type="button"
                                                className={classNames("user-spaces__option", {
                                                    "is-active": band.id === selectedBandId,
                                                })}
                                                onClick={() => handleSelect(band.id)}
                                                role="menuitemradio"
                                                aria-checked={band.id === selectedBandId}
                                            >
                                                <span className="user-spaces__option-initial" aria-hidden="true">
                                                    {optionIconUrl ? (
                                                        <img
                                                            src={optionIconUrl}
                                                            alt=""
                                                            className="user-spaces__option-image"
                                                        />
                                                    ) : getInitials(band.name)}
                                                </span>
                                                <span className="user-spaces__option-name">{band.name}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="user-spaces__empty">{t("bands.userSpaces.empty")}</p>
                        )}
                    </div>
                    <div className="user-spaces__actions">
                        {onCreateBand && (
                            <button
                                type="button"
                                className="user-spaces__create"
                                onClick={handleCreate}
                                role="menuitem"
                                aria-label={t("bands.userSpaces.createAria")}
                            >
                                <span aria-hidden="true">＋</span>
                                <span>{t("bands.userSpaces.create")}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

UserSpaces.propTypes = {
    bands: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            iconUrl: PropTypes.string,
        })
    ).isRequired,
    selectedBandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectBand: PropTypes.func.isRequired,
    onCreateBand: PropTypes.func,
};
